<template>
  <div>
    <ViewLoadingMessage :loading="loading.orderDetail || prefetchLoading"/>

    <template v-if="currentOrder && !loading.orderDetail && !prefetchLoading">
      <!-- Order Summary -->
      <OrderSummary
          :order="currentOrder"
          :loading="loading.updateStatus"
          @mark-order-as-printed="markOrderAsPrinted"
          @mark-order-as-to-process="markOrderAsToProcess"
          @bulk-select="handleBulkSelect"
          @open-print-summary="openMultiPrintSummary"
      />

      <!-- Printable Items (Bundles and Single Items) -->
      <div v-if="printableGroupedOrderItems.length > 0" class="mb-3">
        <template v-for="item in printableGroupedOrderItems">
          <!-- Bundle -->
          <BundleItem
            v-if="item.isBundleGroup"
            :key="'bundle-' + item.bundle.id"
            :bundle-group="item"
            :order="currentOrder"
          >
            <template #default="{ items }">
              <OrderItem
                v-for="bundleItem in items"
                :key="bundleItem.id"
                :item="bundleItem"
                :order="currentOrder"
                :can-print="hasPerm('edge.print.print')"
                @print-completed="onPrintCompleted"
                @zoom="showMediaZoom"
                @open-print-summary="openMultiPrintSummary"
              />
            </template>
          </BundleItem>

          <!-- Single Item (not part of bundle) -->
          <OrderItem
            v-else
            :key="'item-' + item.id"
            :item="item"
            :order="currentOrder"
            :can-print="hasPerm('edge.print.print')"
            @print-completed="onPrintCompleted"
            @zoom="showMediaZoom"
            @open-print-summary="openMultiPrintSummary"
          />
        </template>
      </div>

    </template>

    <!-- Print Jobs for this Order -->
    <OrderPrintJobs
        v-if="currentOrder"
        @zoom="showMediaZoom"
    />

    <!-- Media Zoom Modal -->
    <MediaZoomModal
        ref="mediaZoomModal"
    />

    <!-- Selection Bar -->
    <SelectionBar
        v-if="currentOrder"
        @open-print-summary="openMultiPrintSummary"
    />

    <!-- Multi-item Print Summary Modal -->
    <PrintSummaryModal
        ref="printSummaryModal"
        @print-completed="onPrintCompleted"
    />
  </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import ViewLoadingMessage from '@/components/ViewLoadingMessage.vue'
import OrderSummary from './OrderSummary.vue'
import OrderItem from './OrderItem.vue'
import BundleItem from '@/domain/edgeOrders/shared/BundleItem.vue'
import OrderPrintJobs from './OrderPrintJobs.vue'
import MediaZoomModal from '@/domain/edgeOrders/shared/MediaZoomModal.vue'
import SelectionBar from './SelectionBar.vue'
import PrintSummaryModal from './PrintSummaryModal.vue'
import OrderStatus, {getOrderStatusLabel, getOrderStatusColor} from '@/domain/core/constant/orderStatus'
import Filters from '@/domain/core/utils/filters'

export default {
  name: 'EdgeOrderDetail',
  components: {
    ViewLoadingMessage,
    OrderSummary,
    OrderItem,
    BundleItem,
    OrderPrintJobs,
    MediaZoomModal,
    SelectionBar,
    PrintSummaryModal
  },
  data() {
    return {
      prefetchLoading: false
    }
  },
  computed: {
    ...mapGetters('edgePrints', [
      'currentOrder',
      'orderItems',
      'printableOrderItems',
      'printableGroupedOrderItems',
      'loading',
      'isProcessingOrder',
      'isCompletedOrder',
      'picaServiceId',
      'getTotalMediaCounts'
    ]),

    allPrintableItemsCompleted() {
      const counts = this.getTotalMediaCounts
      return counts.total > 0 && counts.remaining === 0
    }
  },
  methods: {
    ...mapActions('edgePrints', [
      'setPicaServiceId',
      'fetchOrderDetail',
      'fetchPrintJobsForOrder',
      'updateOrderStatus',
      'clearCurrentOrder',
      'prefetchAllOrderMedia',
      'printAllMissingMedia',
      'clearAllSelections'
    ]),
    ...mapActions('login', [
      'fetchConfig'
    ]),
    async loadOrderDetail() {
      const orderId = this.$route.params.orderId
      try {
        // Load order details
        await this.fetchOrderDetail(orderId)

        // Check if we need to print missing items
        if (this.$route.query.print === 'missing' && this.isProcessingOrder) {
          // Wait a moment for the order to be fully loaded
          await this.$nextTick()
          await this.printMissing()
        }

        // Prefetch all media for items with media (both printable and digital)
        const itemsWithMedia = this.orderItems.filter(item =>
            item.is_printable ||
            (item.media_ids && item.media_ids.length > 0) ||
            (item.gallery_ids && item.gallery_ids.length > 0)
        )

        if (itemsWithMedia.length > 0) {
          this.prefetchLoading = true

          try {
            const result = await this.prefetchAllOrderMedia()

            if (!result.success) {
              console.warn('Some media failed to load:', result)
            }

            // Force OrderSummary to recompute after media is loaded
            await this.$nextTick()
            this.$forceUpdate()
          } catch (error) {
            console.error('Failed to prefetch media:', error)
            // Continue anyway - the app will still work with lazy loading
          } finally {
            this.prefetchLoading = false
          }
        }
      } catch (error) {
        this.notifyError(this.$pgettext('edge-prints.detail', 'Failed to load order details'))
        this.$router.push({name: 'edgeOrderList', params: {picaServiceId: this.picaServiceId}})
      }
    },
    getProductName(item) {
      if (item.bundle) {
        return item.bundle.name
      } else if (item.product) {
        return item.product.name
      }
      return '-'
    },

    async markOrderAsPrinted() {
      // Check if all photos are printed
      const counts = this.getTotalMediaCounts
      const allPrinted = counts.total > 0 && counts.remaining === 0

      let message, color
      if (allPrinted) {
        // All photos printed - standard confirmation
        message = this.$pgettext('edge-prints.detail', 'Are you sure all photos have been printed and you want to mark this order as completed?')
        color = 'success'
      } else {
        // Not all photos printed - warning confirmation
        const remaining = counts.remaining
        message = this.$pgettext('edge-prints.detail', 'WARNING: There are still %{count} photos that have not been printed. Are you sure you want to mark this order as completed?').replace('%{count}', remaining)
        color = 'warning'
      }

      await this.confirm({
        title: this.$pgettext('edge-prints.detail', 'Mark Order as Printed'),
        message: message,
        color: color,
        cb_confirm: async () => {
          try {
            await this.updateOrderStatus({
              orderId: this.currentOrder.id,
              status: OrderStatus.COMPLETED.value
            })
            await this.notifySuccess(this.$pgettext('edge-prints.detail', 'Order marked as printed'))

            // Redirect to order list
            this.$router.push({
              name: 'edgePrintOrderList',
              params: {
                picaServiceId: this.picaServiceId
              }
            })
          } catch (error) {
            await this.notifyError(this.$pgettext('edge-prints.detail', 'Failed to update order status'))
          }
        }
      })
    },

    async markOrderAsToProcess() {
      await this.confirm({
        title: this.$pgettext('edge-prints.detail', 'Mark to Process'),
        message: this.$pgettext('edge-prints.detail', 'Are you sure you want to mark this order as "To Process"? This will reopen the order for printing.'),
        cb_confirm: async () => {
          try {
            await this.updateOrderStatus({
              orderId: this.currentOrder.id,
              status: OrderStatus.PROCESSING.value
            })
            await this.notifySuccess(this.$pgettext('edge-prints.detail', 'Order marked as to process'))
          } catch (error) {
            await this.notifyError(this.$pgettext('edge-prints.detail', 'Failed to update order status'))
          }
        }
      })
    },

    handleBulkSelect({orderItemId, selectedIds}) {
      this.$store.commit('edgePrints/setOrderItemMediaSelection', {
        orderItemId,
        selectedIds
      })
    },
    onPrintCompleted() {
      // No need to fetch - print jobs are already added via addPrintJob mutation
      // Clear all selections after successful print
      this.clearAllSelections()
    },
    async printMissing() {
      try {
        const result = await this.printAllMissingMedia()

        if (result.totalPrinted > 0) {
          this.notifySuccess(
              this.$ngettext(
                  'Print job created for %{count} photo',
                  'Print jobs created for %{count} photos',
                  result.totalPrinted
              ).replace('%{count}', result.totalPrinted)
          )
          // Clear the print query parameter
          this.$router.replace({query: {}})
        } else {
          this.notifyInfo(this.$pgettext('edge-prints.detail', 'All photos have already been printed'))
        }
      } catch (error) {
        console.error('Failed to print missing:', error)
        this.notifyError(this.$pgettext('edge-prints.detail', 'Failed to create print jobs'))
      }
    },
    getStatusLabel(status) {
      return getOrderStatusLabel(status)
    },
    getStatusColor(status) {
      return getOrderStatusColor(status)
    },
    getStatusBadgeColor(status) {
      const color = this.getStatusColor(status)
      // Map to lighter badge colors
      const badgeColorMap = {
        'primary': 'light',
        'success': 'light',
        'warning': 'light',
        'danger': 'light',
        'secondary': 'light',
        'dark': 'light'
      }
      return badgeColorMap[color] || 'light'
    },

    showMediaZoom(media) {
      this.$refs.mediaZoomModal.open(media)
    },

    openMultiPrintSummary(groupedItems) {
      if (!groupedItems || groupedItems.length === 0) return

      // Pass the grouped items directly to the modal
      // The modal will handle both single and multiple items
      this.$refs.printSummaryModal.open(groupedItems)
    }
  },

  async beforeRouteLeave(to, from, next) {
    // Warn if all photos are printed but order not marked as printed
    if (this.allPrintableItemsCompleted && !this.isCompletedOrder) {
      await this.confirm({
        title: this.$pgettext('edge-prints.detail', 'Leave page?'),
        message: this.$pgettext('edge-prints.detail', 'All photos have been printed but the order has not been marked as completed. Do you really want to leave?'),
        color: 'warning',
        cb_confirm: () => {
          next()
        },
        cb_cancel: () => {
          next(false)
        }
      })
    } else {
      next()
    }
  },

  async mounted() {
    const picaServiceId = this.$route.params.picaServiceId

    // Set pica service ID in store
    await this.setPicaServiceId(picaServiceId)

    // Fetch edge config for permissions
    try {
      await this.fetchConfig({
        type: 'edge_print',
        id: picaServiceId
      })
    } catch (error) {
      console.error('Failed to fetch edge config:', error)
    }

    await this.loadOrderDetail()
  },
  beforeDestroy() {
    this.clearCurrentOrder()
  },
  filters: {
    currency: Filters.currency,
    formatDate: Filters.formatDate
  }
}
</script>

<style scoped>
.main-header {
  font-size: 1.75rem;
  font-weight: 600;
}

.table-compact td,
.table-compact th {
  padding: 0.5rem 0.75rem;
}

.list-striped dt,
.list-striped dd {
  padding: 0.75rem 1rem;
}

.list-striped dt:nth-of-type(odd),
.list-striped dd:nth-of-type(odd) {
  background-color: #f8f9fa;
}

.custom-table {
  font-size: 0.875rem;
}

.font-weight-medium {
  font-weight: 500;
}

.print-job-item:last-child {
  border-bottom: none !important;
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}
</style>